/**
 * Підбір основного засобу для рядка-ОЗ реалізації / рахунку покупцю
 * (0445, 2026-08-29): активи в експлуатації (OPERATING/PARKED/CONSERVATION/
 * REPAIR_LONG) з колонками інв.№ — назва — первісна — знос — залишкова.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { assetsRegistry, ASSET_STATUS_UA, type AssetRegistryItem } from '@/api/assetsRegistry';
import TchTable from '@/shared/components/TchTable';

export const FA_SALEABLE_STATUSES = ['OPERATING', 'PARKED', 'CONSERVATION', 'REPAIR_LONG'];

const fmt = (v: string | number | null | undefined) =>
  Number(v ?? 0).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

interface Props {
  organizationId: string;
  /** Уже додані в документ — ховаємо. */
  excludeIds?: string[];
  onPick: (asset: AssetRegistryItem) => void;
  onClose: () => void;
}

export default function FixedAssetPickerModal({ organizationId, excludeIds = [], onPick, onClose }: Props) {
  const [q, setQ] = useState('');
  const listQ = useQuery({
    queryKey: ['fa-picker', organizationId],
    queryFn: () => assetsRegistry.list({ organization_id: organizationId, kind: 'FIXED_ASSET', limit: 500 }),
    enabled: !!organizationId,
  });
  const rows = useMemo(() => {
    const ex = new Set(excludeIds);
    const s = q.trim().toLowerCase();
    return (listQ.data?.items ?? [])
      .filter((a) => FA_SALEABLE_STATUSES.includes(a.status) && !ex.has(a.id))
      .filter((a) => !s || a.name.toLowerCase().includes(s) || a.inventory_number.toLowerCase().includes(s));
  }, [listQ.data, q, excludeIds]);

  return (
    <div onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'var(--overlay)',
        display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ width: '90%', maxWidth: 960, maxHeight: '85vh', background: 'var(--bg-panel)',
          borderRadius: 'var(--radius)', boxShadow: 'var(--shadow-lg)', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '1rem', borderBottom: '1px solid var(--border)', display: 'flex',
          justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>Вибрати основний засіб</h3>
          <button onClick={onClose} style={{ padding: '0.3rem 0.6rem' }}>×</button>
        </div>
        <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid var(--border)' }}>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="пошук: інв. № або назва"
            autoFocus style={{ width: '100%' }} />
        </div>
        <div style={{ overflow: 'auto', padding: '0 1rem 1rem' }}>
          <TchTable style={{ marginTop: '0.5rem' }}>
            <thead>
              <tr style={{ textTransform: 'none' }}>
                <th style={{ width: 110 }}>Інв. №</th>
                <th>Назва</th>
                <th style={{ width: 120 }}>Стан</th>
                <th style={{ width: 120, textAlign: 'right' }}>Первісна</th>
                <th style={{ width: 120, textAlign: 'right' }}>Знос</th>
                <th style={{ width: 120, textAlign: 'right' }}>Залишкова</th>
              </tr>
            </thead>
            <tbody>
              {listQ.isLoading && <tr><td colSpan={6} style={{ color: 'var(--muted)' }}>Завантаження…</td></tr>}
              {!listQ.isLoading && rows.length === 0 && (
                <tr><td colSpan={6} style={{ color: 'var(--muted)' }}>Немає активів в експлуатації</td></tr>
              )}
              {rows.map((a) => (
                <tr key={a.id} onClick={() => onPick(a)} style={{ cursor: 'pointer' }}
                  title="Клік — додати у документ">
                  <td>{a.inventory_number}</td>
                  <td>{a.name}</td>
                  <td>{ASSET_STATUS_UA[a.status] ?? a.status}</td>
                  <td style={{ textAlign: 'right' }}>{fmt(a.primary_cost)}</td>
                  <td style={{ textAlign: 'right' }}>{fmt(a.accumulated_depreciation)}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmt(a.residual_value)}</td>
                </tr>
              ))}
            </tbody>
          </TchTable>
        </div>
      </div>
    </div>
  );
}
